import { ProFormColumnsType } from '@ant-design/pro-components';
import { Button, Tooltip } from 'antd';
import { IAddressResponseDetails } from '../../../../types/address';
import { TypedFormatMessageFn } from '../../../../types/intl';
import countries from './countriesList';

export type AddressFormSchemaProps = {
  formatMessage: TypedFormatMessageFn;
  postcode: string;
  setPostcode: (val: string) => void;
  isFetching: boolean;
  fetchAddressesByPostcode: () => void;
  error: unknown;
  addressOptions: IAddressResponseDetails[];
  isEditable?: boolean;
};

const manualAddressToggle = (props: AddressFormSchemaProps, postcode: string): ProFormColumnsType[] => [
  {
    hideInForm: props.isEditable === false,
    dataIndex: ['address', 'isManual'],
    valueType: 'switch',
    formItemProps: {
      valuePropName: 'checked',
    },
    fieldProps: {
      checkedChildren: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.manualAddress.button.label.0' }),
      unCheckedChildren: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.manualAddress.button.label.1' }),
      onChange: () => {
        props.setPostcode(postcode);
      },
    },
  },
];

const postcodeFormItem = (props: AddressFormSchemaProps, isManual: boolean): ProFormColumnsType[] => [
  {
    title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.postcode.label' }),
    dataIndex: ['address', 'postcode'],
    valueType: 'text',
    colProps: { span: isManual ? 24 : 18 },
    fieldProps: {
      placeholder: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.postcode.placeholder' }),
      onChange: val => props.setPostcode(val.currentTarget.value),
    },
    formItemProps: {
      labelCol: { span: isManual ? 6 : 8 },
      rules: [{ required: true, message: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.postcode.error' }) }],
    },
  },
];

const isValidPostcode = (pc: string): boolean => /^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i.test(pc);

export const addressFormSchema = (props: AddressFormSchemaProps): ProFormColumnsType[] => [
  {
    valueType: 'dependency',
    name: ['address'],
    columns: val => {
      const { isManual, postcode } = val.address || {};
      if (isManual) {
        return [
          ...postcodeFormItem(props, isManual),
          ...manualAddressToggle(props, postcode),
          {
            title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.address1.label' }),
            dataIndex: ['address', 'addressLine1'],
            valueType: 'text',
            fieldProps: {
              placeholder: '',
            },
            formItemProps: {
              rules: [{ required: true, message: props.formatMessage({ id: 'common.error.requiredField' }) }],
            },
          },
          {
            title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.address2.label' }),
            dataIndex: ['address', 'addressLine2'],
            valueType: 'text',
            fieldProps: {
              placeholder: '',
            },
          },
          {
            title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.city.label' }),
            dataIndex: ['address', 'town'],
            valueType: 'text',
            fieldProps: {
              placeholder: '',
            },
            formItemProps: {
              rules: [{ required: true, message: props.formatMessage({ id: 'common.error.requiredField' }) }],
            },
          },
          {
            title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.country.label' }),
            dataIndex: ['address', 'country'],
            valueType: 'select',
            fieldProps: {
              options: countries,
              showSearch: true,
            },
            formItemProps: {
              rules: [{ required: true, message: props.formatMessage({ id: 'common.error.requiredField' }) }],
            },
          },
        ];
      }
      return [
        {
          valueType: 'group',
          columns: [
            ...postcodeFormItem(props, isManual),
            {
              title: ' ',
              fieldProps: {
                disabled: !props.postcode || props.postcode?.length === 0,
              },
              formItemProps: {
                colon: false,
              },
              width: '100%',
              colProps: { span: 6 },
              renderFormItem: () => (
                <Tooltip
                  title={
                    isValidPostcode(props.postcode)
                      ? ''
                      : props.formatMessage({ id: 'btlApplicationForm.PROPERTY.postcode.tooltip.invalid' })
                  }
                >
                  <Button
                    type='primary'
                    loading={props.isFetching}
                    disabled={!isValidPostcode(props.postcode)}
                    onClick={() => props.fetchAddressesByPostcode()}
                  >
                    {props.formatMessage({ id: 'btlApplicationForm.PROPERTY.postcode.button.label' })}
                  </Button>
                </Tooltip>
              ),
            },
          ],
        },
        {
          hideInForm: !props.error,
          formItemProps: {
            className: 'inline',
          },
          renderFormItem: () => <div className={'-mt-10 text-red-600'}>{`${props.error} occurred`}</div>,
        },
        {
          title: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.fullAddress.label' }),
          dataIndex: ['address', 'fullAddress'],
          valueType: 'select',
          key: `fullAddress-${props.postcode}`,
          fieldProps: {
            options: props.addressOptions.map(value => ({
              label: value.fullAddress,
              value: JSON.stringify(value),
            })),
            disabled: props.addressOptions.length === 0,
            placeholder:
              props.addressOptions.length === 0
                ? props.formatMessage({ id: 'btlApplicationForm.PROPERTY.fullAddress.placeholder.0' })
                : props.formatMessage({ id: 'btlApplicationForm.PROPERTY.fullAddress.placeholder.1' }),
          },
          formItemProps: {
            rules: [
              {
                required: true,
                message: props.formatMessage({ id: 'btlApplicationForm.PROPERTY.fullAddress.error' }),
              },
            ],
          },
        },
        ...manualAddressToggle(props, postcode),
      ];
    },
  },
];
